import { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Navbar from "./navbar";
import Footer from "./Footer";
import { BASE_URL } from "../apiConfig";
import RippleWaveLoader from "./ui/RippleWaveLoader";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { jsPDF } from "jspdf";

interface CompanyDetails {
  company_name: string;
  website: string;
  founders: Array<{ name: string; email: string }>;
  contact_numbers: Array<{
    number: string;
    linkedin: string;
  }>;
  profile: string;
  solutions_relevant_to_query: Array<{
    title: string;
    description: string;
    link: string;
  }>;
  clients_or_references: Array<{
    name: string;
    details: string;
  }>;
  business_collaterals: Array<{
    type: string;
    url: string;
  }>;
  sources: string[];
}

const SolutionCompanyDetailPage = () => {
  const location = useLocation();
  const { companyName } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<CompanyDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Hunter.io contact enrichment
  const [hunterContacts, setHunterContacts] = useState<any>(null);
  const [hunterLoading, setHunterLoading] = useState(false);
  const [hunterError, setHunterError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${BASE_URL}/dedicated_solution_provider_detail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company_name: companyName,
            user_query: location.state?.title || location.state?.description || ''
          })
        });

        if (!response.ok) {
          throw new Error('Failed to fetch company details');
        }

        const data = await response.json();

        if (data.status === "success") {
          setDetails(data.details);
          // Save to sessionStorage
          sessionStorage.setItem(`companyDetails_${companyName}`, JSON.stringify(data.details));
        } else {
          throw new Error(data.message || 'Failed to fetch company details');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    // Always fetch fresh data (don't use stale cache across different companies)
    fetchCompanyDetails();
  }, [companyName, location.state]);

  const handleBack = () => {
    // First save the current state in sessionStorage
    if (details) {
      sessionStorage.setItem('previousDetails', JSON.stringify(details));
    }
    // Then navigate back
    navigate(-1);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // no-op
    }
  };

  const handleDownloadPDF = async () => {
    if (!details) return;
    setDownloading(true);
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = 210, pageHeight = 297, margin = 15;
      const usableWidth = pageWidth - margin * 2;
      const lineH = 6;
      let y = margin;

      const checkPage = (extra = lineH) => {
        if (y + extra > pageHeight - margin) {
          pdf.addPage();
          y = margin;
        }
      };
      const text = (t: string, size = 10, style: 'normal' | 'bold' = 'normal', color: [number,number,number] = [20,20,20]) => {
        pdf.setFont('helvetica', style as any);
        pdf.setFontSize(size);
        pdf.setTextColor(...color);
        const lines = pdf.splitTextToSize(t || '-', usableWidth);
        lines.forEach((ln: string) => { checkPage(); pdf.text(ln, margin, y); y += lineH; });
      };
      const header = (t: string) => { checkPage(lineH*2); y += 2; text(t, 13, 'bold', [255,84,31]); y += 2; };

      // Title
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16);
      pdf.text(details.company_name || 'Company', pageWidth/2, y, { align: 'center' }); y += 8;

      header('Overview');
      text(`Website: ${details.website || '-'}`, 10, 'normal', [60,60,60]);
      if (details.profile) text(details.profile, 10, 'normal', [40,40,40]);

      if (details.founders?.length) {
        header('Founders');
        details.founders.forEach(f => text(`• ${f.name}${f.email ? ` (${f.email})` : ''}`));
      }

      if (details.contact_numbers?.length) {
        header('Contact Numbers');
        details.contact_numbers.forEach(c => {
          text(`• ${c.number}`);
          if (c.linkedin) text(`  LinkedIn: ${c.linkedin}`, 9, 'normal', [100,100,100]);
        });
      }

      if (details.solutions_relevant_to_query?.length) {
        header('Related Solutions');
        details.solutions_relevant_to_query.forEach(s => {
          text(`• ${s.title}`, 11, 'bold', [60,60,60]);
          if (s.description) text(s.description, 10, 'normal', [80,80,80]);
          if (s.link) text(s.link, 9, 'normal', [100,100,100]);
        });
      }

      if (details.clients_or_references?.length) {
        header('Clients & References');
        details.clients_or_references.forEach(c => text(`• ${c.name}: ${c.details || '-'}`));
      }

      if (details.business_collaterals?.length) {
        header('Business Collaterals');
        details.business_collaterals.forEach(b => text(`• ${b.type}: ${b.url}`));
      }

      if (details.sources?.length) {
        header('Sources');
        details.sources.forEach(s => text(`• ${s}`));
      }

      const fname = `${(details.company_name || 'company').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}_${new Date().toISOString().slice(0,10)}.pdf`;
      pdf.save(fname);
    } catch (_e) {
      // no-op
    } finally {
      setDownloading(false);
    }
  };

  const fetchHunterContacts = async (website: string) => {
    try {
      setHunterLoading(true);
      setHunterError(null);
      const res = await fetch(`${BASE_URL}/api/v1/startups/contacts?domain=${encodeURIComponent(website)}`);
      const data = await res.json();
      if (data.status === "success") {
        setHunterContacts(data.data);
        if (!data.data.emails?.length) {
          setHunterError("No verified contacts found for this domain.");
        }
      } else {
        setHunterError(data.message || "Failed to fetch contacts");
      }
    } catch {
      setHunterError("Failed to fetch contacts. Please try again.");
    } finally {
      setHunterLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <RippleWaveLoader />
        </div>
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-white">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-4">Error Loading Company Details</h2>
            <p className="text-gray-400">{error || 'Company details not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const founders = details.founders || [];
  const contacts = details.contact_numbers || [];
  const solutions = details.solutions_relevant_to_query || [];
  const clients = details.clients_or_references || [];
  const collaterals = details.business_collaterals || [];
  const sources = details.sources || [];

  return (
    <div
      className="min-h-screen relative flex flex-col overflow-x-hidden"
      style={{
        background: "radial-gradient(35% 8% at 50% 0%, rgba(255, 84, 31, 0.5) 0%, rgba(0, 0, 0, 0) 100%), black",
        color: "white",
      }}
    >
      <Navbar />

      {/* Decorative background elements */}
      <div className="absolute bg-dots" style={{
        width:"1106px",height:"500px",top:"-12px",left:"194px",
        backgroundImage:"url('/dots.png')",backgroundSize:"contain",
        backgroundRepeat:"no-repeat",pointerEvents:"none",zIndex:0
      }}/>

      <div className="max-w-[1440px] mx-auto px-4 md:px-12 lg:px-24 pt-32 pb-20 md:pb-32 relative z-10">
        {/* Header Section - Single Row Layout */}
        <div className="mb-10">
          {/* Desktop Layout - Single Row */}
          <div className="hidden md:flex items-center justify-between gap-6 mb-6">
            {/* Left: Back Button */}
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium font-[Sora] text-white/90 bg-white/5 hover:bg-white/10 rounded-full border border-white/10 transition-all duration-200 shrink-0"
            >
              <ArrowBackIcon className="w-4 h-4" />
              Back
            </button>

            {/* Center: Company Name */}
            <h1
              className="text-2xl lg:text-3xl font-bold font-[Sora] bg-gradient-to-r from-white to-neutral-300 bg-clip-text text-transparent text-center flex-1 truncate px-4"
              title={details.company_name}
            >
              {details.company_name}
            </h1>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-3 shrink-0">
              {details.website ? (
                <a
                  href={details.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium font-[Sora] !text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-full border border-orange-500/30 transition-all duration-200 shadow-lg shadow-orange-500/20"
                >
                  <OpenInNewIcon className="w-4 h-4" />
                  Visit Website
                </a>
              ) : null}
              <button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold font-[Sora] bg-white/5 hover:bg-white/10 rounded-full border border-white/15 disabled:opacity-60 transition-all duration-200"
              >
                <DownloadIcon className="w-4 h-4" />
                {downloading ? 'Preparing...' : 'Download PDF'}
              </button>
            </div>
          </div>

          {/* Mobile Layout - Stacked */}
          <div className="md:hidden space-y-4">
            {/* Back button */}
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium font-[Sora] text-white/90 bg-white/5 hover:bg-white/10 rounded-full border border-white/10 transition-all duration-200"
            >
              <ArrowBackIcon className="w-4 h-4" />
              Back
            </button>

            {/* Company Name */}
            <h1
              className="text-3xl font-bold font-[Sora] bg-gradient-to-r from-white to-neutral-300 bg-clip-text text-transparent"
            >
              {details.company_name}
            </h1>
          </div>
        </div>

        {/* Top grid: Overview + Key Contacts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Overview */}
          <div
            className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/10 hover:-translate-y-1 cursor-pointer"
            style={{
              background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
            }}
          >
            <h2 className="text-2xl font-semibold font-[Sora] mb-6 text-white">Company Overview</h2>
            <div className="space-y-5">
              <InfoRow label="Website" value={details.website} isLink />
              <ExpandableText label="Profile" text={details.profile} />
              {founders.length ? (
                <ExpandableSection title="Founders">
                  <div className="space-y-4">
                    {founders.map((f, idx) => (
                      <div key={`${f.name}-${idx}`} className="flex items-start justify-between gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                        <div className="flex-1">
                          <div className="font-semibold font-[Sora] text-white">{f.name}</div>
                          {f.email ? <div className="text-sm text-neutral-400 mt-1 font-[Sora]">{f.email}</div> : null}
                        </div>
                        {f.email ? (
                          <button
                            className="flex items-center gap-2 px-3 py-2 text-xs font-[Sora] bg-orange-500/10 hover:bg-orange-500/20 rounded-lg border border-orange-500/30 transition-all duration-200"
                            onClick={() => copyToClipboard(f.email)}
                          >
                            <ContentCopyIcon className="w-3.5 h-3.5" /> Copy
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </ExpandableSection>
              ) : null}
            </div>
          </div>

          {/* Contacts */}
          <div
            className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-1 cursor-pointer"
            style={{
              background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
            }}
          >
            <h2 className="text-2xl font-semibold font-[Sora] mb-6 text-white">Key Contacts</h2>
            {contacts.filter(c => c.number || c.linkedin).length ? (
              <div className="space-y-4">
                {contacts.filter(c => c.number || c.linkedin).map((contact, idx) => (
                  <div key={idx} className="space-y-3">
                    {/* Phone Number Card — only show if number exists */}
                    {contact.number ? (
                    <div className="flex items-center justify-between gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:border-orange-500/30 transition-all duration-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center border border-orange-500/30">
                          <span className="text-xl">📞</span>
                        </div>
                        <div>
                          <div className="text-xs font-[Sora] text-neutral-400 mb-1">Phone Number</div>
                          <div className="font-semibold font-[Sora] text-white">{contact.number}</div>
                        </div>
                      </div>
                      <button
                        className="flex items-center gap-2 px-3 py-2 text-xs font-[Sora] bg-orange-500/10 hover:bg-orange-500/20 rounded-lg border border-orange-500/30 transition-all duration-200"
                        onClick={() => copyToClipboard(contact.number)}
                      >
                        <ContentCopyIcon className="w-3.5 h-3.5" /> Copy
                      </button>
                    </div>
                    ) : null}

                    {/* LinkedIn Card */}
                    {contact.linkedin ? (
                      <a
                        href={contact.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between gap-4 p-4 bg-white/5 rounded-xl border border-white/10 hover:border-blue-500/30 transition-all duration-200 group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/30">
                            <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                          </div>
                          <div>
                            <div className="text-xs font-[Sora] text-neutral-400 mb-1">LinkedIn Profile</div>
                            <div className="font-medium font-[Sora] text-blue-400 group-hover:text-blue-300 transition-colors">View Profile</div>
                          </div>
                        </div>
                        <OpenInNewIcon className="w-5 h-5 text-neutral-400 group-hover:text-blue-400 transition-colors" />
                      </a>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-neutral-400 text-sm font-[Sora]">No contact numbers available.</div>
            )}
          </div>
        </div>

        {/* Hunter.io Verified Contacts */}
        {details.website && (
          <div
            className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-orange-500/10 mb-8"
            style={{ background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)" }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold font-[Sora] text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-[#FF541F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Verified Contacts
              </h2>
              {!hunterContacts && !hunterLoading && (
                <button
                  onClick={() => fetchHunterContacts(details.website)}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold font-[Sora] bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 rounded-full text-white transition-all duration-200"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Find Contacts
                </button>
              )}
            </div>

            {hunterLoading && (
              <div className="flex items-center gap-3 text-gray-400 py-4">
                <svg className="animate-spin h-5 w-5 text-[#FF541F]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm font-[Sora]">Looking up contacts via Hunter.io...</span>
              </div>
            )}

            {hunterError && !hunterContacts?.emails?.length && (
              <div className="flex items-center gap-2 text-yellow-400 text-sm py-2 font-[Sora]">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {hunterError}
              </div>
            )}

            {hunterContacts?.emails?.length > 0 && (
              <div className="space-y-4">
                {hunterContacts.pattern && (
                  <p className="text-gray-500 text-xs font-[Sora]">
                    Email pattern: <span className="text-gray-400 font-mono">{hunterContacts.pattern}@{hunterContacts.domain}</span>
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {hunterContacts.emails.map((c: any, i: number) => (
                    <div key={i} className="p-4 rounded-xl border border-white/10 bg-white/[0.03] flex items-start gap-3 hover:border-orange-500/30 transition-all duration-200">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {(c.first_name || c.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        {(c.first_name || c.last_name) && (
                          <p className="font-semibold text-white text-sm font-[Sora] truncate">
                            {[c.first_name, c.last_name].filter(Boolean).join(" ")}
                          </p>
                        )}
                        {c.position && <p className="text-gray-400 text-xs font-[Sora] truncate">{c.position}</p>}
                        <a href={`mailto:${c.email}`} className="text-orange-400 hover:text-orange-300 text-sm font-[Sora] flex items-center gap-1 mt-1">
                          <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {c.email}
                        </a>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          {c.confidence != null && (
                            <span className={`text-xs font-[Sora] flex items-center gap-1 ${c.confidence >= 80 ? "text-green-400" : c.confidence >= 50 ? "text-yellow-400" : "text-gray-500"}`}>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                              {c.confidence}%
                            </span>
                          )}
                          {c.linkedin && (
                            <a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                              LinkedIn
                            </a>
                          )}
                          {c.type && (
                            <span className={`text-xs font-[Sora] px-1.5 py-0.5 rounded ${c.type === "personal" ? "bg-green-500/10 text-green-400" : "bg-gray-500/10 text-gray-400"}`}>
                              {c.type}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-gray-600 text-xs font-[Sora] mt-2">Powered by Hunter.io</p>
              </div>
            )}

            {!hunterContacts && !hunterLoading && !hunterError && (
              <p className="text-gray-500 text-sm font-[Sora]">Click "Find Contacts" to look up verified email addresses for this company.</p>
            )}
          </div>
        )}

        {/* Related Solutions */}
        {solutions.length ? (
          <div
            className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/10 hover:-translate-y-1 cursor-pointer mb-8"
            style={{
              background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
            }}
          >
            <h2 className="text-2xl font-semibold font-[Sora] mb-6 text-white">Solutions Relevant to Query</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {solutions.map((s, idx) => (
                <div
                  key={idx}
                  className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-orange-500/30 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/10"
                >
                  <div className="font-semibold font-[Sora] text-lg text-white mb-3">{s.title}</div>
                  <div className="text-neutral-300 text-sm font-[Sora] leading-relaxed mb-4">{s.description}</div>
                  {s.link ? (
                    <a
                      href={s.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-[Sora] text-sm font-medium transition-colors duration-200"
                    >
                      Learn More <OpenInNewIcon className="w-4 h-4" />
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Clients & References */}
        {clients.length ? (
          <div
            className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-green-500/10 hover:-translate-y-1 cursor-pointer mb-8"
            style={{
              background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
            }}
          >
            <h2 className="text-2xl font-semibold font-[Sora] mb-6 text-white">Clients & References</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {clients.map((c, idx) => (
                <div key={idx} className="border-l-4 border-orange-500 pl-5 py-3 bg-white/5 rounded-r-xl">
                  <div className="font-semibold font-[Sora] text-white text-lg">{c.name}</div>
                  {c.details ? <div className="text-sm font-[Sora] text-neutral-300 mt-2 leading-relaxed">{c.details}</div> : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Business Collaterals */}
        {collaterals.length ? (
          <div
            className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/10 hover:-translate-y-1 cursor-pointer mb-8"
            style={{
              background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
            }}
          >
            <h2 className="text-2xl font-semibold font-[Sora] mb-6 text-white">Business Collaterals</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {collaterals.map((doc, idx) => {
                let domain = doc.url;
                try {
                  const urlObj = new URL(doc.url);
                  domain = urlObj.hostname.replace(/^www\./i, "");
                } catch {}
                return (
                  <a
                    key={idx}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-5 bg-white/5 rounded-xl hover:bg-white/10 transition-all duration-200 flex items-center justify-between border border-white/10 hover:border-orange-500/30 group"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
                        alt={`${domain} favicon`}
                        className="w-8 h-8 rounded-lg flex-shrink-0"
                        style={{
                          background: "transparent",
                          objectFit: "cover",
                          filter: "grayscale(100%) opacity(0.95)",
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-orange-400 font-[Sora] font-medium group-hover:text-orange-300 transition-colors">{doc.type}</div>
                        <div className="text-neutral-500 text-xs font-[Sora] truncate mt-1">{doc.url}</div>
                      </div>
                    </div>
                    <OpenInNewIcon className="w-5 h-5 text-neutral-400 group-hover:text-orange-400 transition-colors flex-shrink-0 ml-3" />
                  </a>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Sources */}
        {sources.length ? (
          <div
            className="rounded-2xl p-8 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/10 hover:-translate-y-1 cursor-pointer"
            style={{
              background: "linear-gradient(135deg, rgba(39, 40, 41, 0.6) 0%, rgba(26, 26, 26, 0.8) 100%)",
            }}
          >
            <ExpandableSection title="Sources" defaultExpanded={false}>
              <div className="space-y-3 mt-4">
                {sources.map((src, idx) => {
                  let domain = src;
                  let displayTitle = src;
                  try {
                    const urlObj = new URL(src);
                    domain = urlObj.hostname.replace(/^www\./i, "");
                    // Create a cleaner display title from the URL
                    displayTitle = domain;
                  } catch {}
                  return (
                    <a
                      key={idx}
                      href={src}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-all duration-200 border border-white/10 hover:border-orange-500/30 group"
                    >
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
                        alt={`${domain} favicon`}
                        className="w-8 h-8 rounded flex-shrink-0 mt-0.5"
                        style={{
                          background: "transparent",
                          objectFit: "cover",
                          filter: "grayscale(100%) opacity(0.95)",
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-[Sora] font-medium text-white group-hover:text-orange-400 transition-colors mb-1">
                          {displayTitle}
                        </div>
                        <div className="font-[Sora] text-xs text-neutral-500 break-words leading-relaxed" style={{ wordBreak: 'break-all' }}>
                          {src}
                        </div>
                      </div>
                      <OpenInNewIcon className="w-5 h-5 flex-shrink-0 text-neutral-400 group-hover:text-orange-400 transition-colors mt-0.5" />
                    </a>
                  );
                })}
              </div>
            </ExpandableSection>
          </div>
        ) : null}
      </div>

      {/* Mobile View - Fixed Bottom Bar */}
      <div
        className="
          md:hidden fixed bottom-0 left-0 right-0
          bg-gradient-to-t from-black/95 via-black/85 to-black/60
          border-t border-white/10
          px-3 pt-2
          pb-[calc(0.55rem+env(safe-area-inset-bottom))]
          backdrop-blur-xl
          z-50
          shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.6)]
        "
      >
        <div className="flex gap-2.5 max-w-lg mx-auto">
          {details?.website ? (
            <a
              href={details.website}
              target="_blank"
              rel="noopener noreferrer"
              className="
                flex-1 inline-flex items-center justify-center gap-2 rounded-full py-3
                text-[0.85rem] font-semibold tracking-wide uppercase
                bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700
                transition-all duration-200 active:scale-[0.97]
                border border-orange-500/30
                shadow-lg shadow-orange-500/25
              "
              style={{
                color: "#ffffff",
              }}
            >
              <OpenInNewIcon className="w-4 h-4" />
              Visit Website
            </a>
          ) : null}
          <button
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full py-3 text-[0.85rem] font-semibold tracking-wide uppercase bg-transparent border border-white/15 hover:bg-white/10 hover:border-white/25 transition-all duration-200 disabled:opacity-55 active:scale-[0.97]"
            onClick={handleDownloadPDF}
            disabled={downloading}
            style={{
              backdropFilter: "blur(8px)"
            }}
          >
            <DownloadIcon className="w-4 h-4" />
            {downloading ? 'Loading...' : 'Download'}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Small, consistent helpers (same look-and-feel as CompanyDetailPage)

const InfoRow = ({ label, value, isLink = false }: { label: string; value?: string; isLink?: boolean }) => {
  if (!value) return null;
  return (
    <div className="pb-4 border-b border-white/5 last:border-b-0">
      <div className="text-sm font-[Sora] text-neutral-400 mb-2">{label}</div>
      {isLink && value.startsWith('http') ? (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-orange-400 hover:text-orange-300 font-[Sora] transition-colors duration-200 inline-flex items-center gap-2"
        >
          {value}
          <OpenInNewIcon className="w-4 h-4" />
        </a>
      ) : (
        <div className="text-neutral-200 font-[Sora]">{value}</div>
      )}
    </div>
  );
};

interface ExpandableSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}
const ExpandableSection = ({ title, children, defaultExpanded = false }: ExpandableSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  return (
    <div className="border-b border-white/5 last:border-b-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full py-4 flex items-center justify-between text-left group hover:bg-white/5 rounded-lg px-2 transition-all duration-200"
      >
        <span className="text-lg font-semibold font-[Sora] text-white group-hover:text-orange-400 transition-colors">{title}</span>
        <span className="text-2xl text-orange-400 font-light transition-transform duration-200" style={{ transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)' }}>
          +
        </span>
      </button>
      {isExpanded && <div className="pb-4">{children}</div>}
    </div>
  );
};

const ExpandableText = ({ label, text, threshold = 180 }: { label: string; text?: string; threshold?: number }) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const isLong = text.length > threshold;
  const shown = isLong && !open ? text.slice(0, threshold) + '…' : text;
  return (
    <div className="pb-4 border-b border-white/5 last:border-b-0">
      <div className="text-sm font-[Sora] text-neutral-400 mb-2">{label}</div>
      <div className="text-neutral-200 font-[Sora] leading-relaxed">
        {shown}
        {isLong && (
          <button
            className="ml-2 text-orange-400 hover:text-orange-300 font-[Sora] text-sm font-medium transition-colors duration-200"
            onClick={() => setOpen(!open)}
          >
            {open ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
};

export default SolutionCompanyDetailPage;